import { Routes, Route, Navigate, useParams } from 'react-router-dom'
import { lazy, Suspense } from 'react'
import * as Sentry from '@sentry/react'
import { MainLayout } from '@/components/common/layout/MainLayout'
import { Toaster } from '@/components/common/ui/toaster' // Import Toaster
import { PrivateRoute } from '@/components/features/auth/PrivateRoute'
import { PublicOnlyRoute } from '@/components/features/auth/PublicOnlyRoute'
import { EmailVerificationRoute } from '@/components/features/auth/EmailVerificationRoute'
import { SpaceRedirect } from '@/components/features/auth/SpaceRedirect'
import { PageLoading } from '@/components/common/layout/PageLoading'
import { AnalysisProgressProvider } from '@/contexts/analysis-progress-context'
import { PromptGenerationProvider } from '@/contexts/prompt-generation-context'
import { CombinedProgressIndicator } from '@/components/features/common/CombinedProgressIndicator'
import { WebSocketProvider } from '@/contexts/websocket-context'

// Lazy load page components
const LoginPage = lazy(() => import('@/pages/login'))
// SignupPage実装は保持済み（src/pages/signup.tsx）- UI上でのアクセスのみ制限
const ForgotPasswordPage = lazy(() => import('@/pages/forgot-password'))
const VerifyEmailPage = lazy(() => import('@/pages/verify-email'))
const SpacePage = lazy(() => import('@/pages/space'))
const ContractPlanPage = lazy(() => import('@/pages/contract-plan'))
const ServiceDetailPage = lazy(() => import('@/pages/service-detail'))
const BatchAnalysisResultsPage = lazy(() => import('@/pages/batch-analysis-results'))
const UserSettingsPage = lazy(() => import('@/pages/user-settings'))
// Wrapper component to validate space_id
function SpaceRouteValidator({ children }: { children: React.ReactNode }) {
  const { space_id } = useParams()

  if (space_id === 'login' || space_id === 'signup') {
    return <Navigate to="/login" replace />
  }
  return <>{children}</>
}

// Enhance Routes with Sentry for performance monitoring
const SentryRoutes = Sentry.withSentryRouting(Routes)

function App() {
  return (
    <WebSocketProvider enabled={true}>
      {/* Ensure prompt-generation consumers are wrapped with its provider */}
      <PromptGenerationProvider>
        <AnalysisProgressProvider>
          <div className="h-screen flex flex-col bg-gradient-to-br from-white to-[#646cff10]">
            <Toaster /> {/* Add Toaster component here */}
            <CombinedProgressIndicator /> {/* 統合進行状況表示 */}
            <Suspense fallback={<PageLoading text="Loading..." />}>
              <SentryRoutes>
                <Route
                  path="/login"
                  element={
                    <PublicOnlyRoute>
                      <LoginPage />
                    </PublicOnlyRoute>
                  }
                />
                <Route
                  path="/forgot-password"
                  element={
                    <PublicOnlyRoute>
                      <ForgotPasswordPage />
                    </PublicOnlyRoute>
                  }
                />
                {/* サインアップページは非表示化：アクセス時はログインページにリダイレクト */}
                <Route path="/signup" element={<Navigate to="/login" replace />} />
                <Route
                  path="/verify-email"
                  element={
                    <EmailVerificationRoute>
                      <VerifyEmailPage />
                    </EmailVerificationRoute>
                  }
                />
                <Route
                  path="/settings"
                  element={
                    <PrivateRoute>
                      <MainLayout />
                    </PrivateRoute>
                  }
                >
                  <Route index element={<UserSettingsPage />} />
                </Route>
                {/* Service detail and batch results routes with shared layout */}
                <Route
                  path="/space/:space_id/service/:service_id/*"
                  element={
                    <PrivateRoute>
                      <SpaceRouteValidator>
                        <MainLayout />
                      </SpaceRouteValidator>
                    </PrivateRoute>
                  }
                >
                  <Route index element={<ServiceDetailPage />} />
                  <Route path="batch-results/:batch_id" element={<BatchAnalysisResultsPage />} />
                  <Route path="*" element={<ServiceDetailPage />} />
                </Route>
                <Route
                  path="/space/:space_id"
                  element={
                    <PrivateRoute>
                      <SpaceRouteValidator>
                        <MainLayout />
                      </SpaceRouteValidator>
                    </PrivateRoute>
                  }
                >
                  <Route index element={<SpacePage />} />
                  <Route path="contract-plan" element={<ContractPlanPage />} />
                </Route>
                {/* Root and default space routes */}
                <Route
                  path="/"
                  element={
                    <PrivateRoute>
                      <SpaceRedirect />
                    </PrivateRoute>
                  }
                />
                <Route
                  path="/space"
                  element={
                    <PrivateRoute>
                      <SpaceRedirect />
                    </PrivateRoute>
                  }
                />

                {/* Catch-all for invalid space routes */}
                <Route path="/space/*" element={<Navigate to="/login" replace />} />
                <Route path="*" element={<Navigate to="/" replace />} />
              </SentryRoutes>
            </Suspense>
          </div>
        </AnalysisProgressProvider>
      </PromptGenerationProvider>
    </WebSocketProvider>
  )
}

export default App
